<template>
  <div class="entity-list-page">
    <header class="list-header">
      <div>
        <router-link to="/clients" class="back-link">← Portfolio</router-link>
        <h1>All projects</h1>
        <p class="list-sub">
          {{ filteredProjects.length }}
          {{ filteredProjects.length === 1 ? 'project' : 'projects' }}
          <span v-if="showArchived"> · including archived</span>
        </p>
      </div>
      <button type="button" class="btn btn-primary" @click="showCreateModal = true">
        <i class="fas fa-plus"></i> Create project
      </button>
    </header>

    <div v-if="!loading && !error" class="toolbar">
      <label class="archive-toggle">
        <input
          type="checkbox"
          v-model="showArchived"
          @change="toggleArchivedView"
        />
        <span>Show archived</span>
      </label>
    </div>

    <div v-if="loading" class="state">Loading projects…</div>
    <div v-else-if="error" class="state error">{{ error }}</div>
    <div v-else-if="filteredProjects.length === 0" class="state">
      No projects match.
      <button type="button" class="linkish" @click="showCreateModal = true">Create a project</button>
    </div>
    <ul v-else class="entity-card-grid">
      <li
        v-for="project in filteredProjects"
        :key="project.id"
        class="entity-card"
        :class="{
          'entity-card--archived': project.archived,
          'entity-card--warn': !project.archived && !project.default_comprehensive_check_name,
        }"
      >
        <h3 class="entity-card-title">
          <router-link :to="`/projects/${project.id}`">{{ project.name }}</router-link>
        </h3>
        <p class="entity-card-desc">
          {{ project.description ? truncate(project.description, 120) : 'No description' }}
        </p>
        <dl class="entity-card-meta">
          <div>
            <dt>Check</dt>
            <dd :class="{ 'is-warn': !project.default_comprehensive_check_name }">
              {{ project.default_comprehensive_check_name || 'No check assigned' }}
            </dd>
          </div>
          <div v-if="project.archived" style="margin-top: 0.25rem">
            <dt>Status</dt>
            <dd>Archived</dd>
          </div>
        </dl>
        <div class="entity-card-actions">
          <router-link :to="`/projects/${project.id}`" class="btn btn-primary">Open</router-link>
          <router-link :to="`/projects/${project.id}/settings`" class="btn btn-ghost">Settings</router-link>
        </div>
      </li>
    </ul>

    <div v-if="showCreateModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <h2>Create project</h2>
        <form @submit.prevent="createProject">
          <div class="form-group">
            <label for="new-project-name">Project name *</label>
            <input
              type="text"
              id="new-project-name"
              v-model="projectForm.name"
              class="form-control"
              placeholder="e.g., ABC Construction — Insurance Compliance"
              required
              maxlength="255"
            >
          </div>

          <div class="form-group">
            <label for="new-project-description">What will you analyze? *</label>
            <textarea
              id="new-project-description"
              v-model="projectForm.description"
              class="form-control"
              rows="3"
              placeholder="Documents and requirements you’ll check"
              required
              maxlength="1000"
            ></textarea>
          </div>

          <div class="form-group">
            <label for="client">Client *</label>
            <select id="client" v-model="projectForm.client" class="form-control" required>
              <option value="">Select a client…</option>
              <option v-for="client in clients" :key="client.id" :value="client.id">
                {{ client.name }}
              </option>
            </select>
          </div>

          <p class="form-hint">Next: assign the compliance check in project settings.</p>

          <div class="modal-actions">
            <button type="button" class="btn btn-ghost" @click="closeModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="!isFormValid">
              Create &amp; configure
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'ProjectsList',
  data() {
    return {
      projects: [],
      showArchived: false,
      showCreateModal: false,
      projectForm: {
        name: '',
        description: '',
        client: null
      },
      loading: true,
      error: null,
      notification: {
        show: false,
        message: '',
        type: 'success'
      },
      showEditModal: false,
      editingProject: {
        id: null,
        name: '',
        description: ''
      },
      clients: [],
      showClientModal: false,
      clientForm: {
        name: '',
        email: '',
        phone: '',
        description: '',
        industry_type: '',
        business_type: '',
        status: 'ACTIVE'
      }
    }
  },
  computed: {
    isFormValid() {
      return this.projectForm.name.trim() &&
             this.projectForm.description.trim() &&
             this.projectForm.client;
    },
    filteredProjects() {
      if (this.showArchived) {
        return this.projects;
      }
      return this.projects.filter(project => !project.archived);
    }
  },
  created() {
    this.loading = true;
    this.fetchProjects();
    this.fetchClients();
  },
  methods: {
    truncate(text, max = 100) {
      if (!text) return '';
      const t = String(text).trim();
      if (t.length <= max) return t;
      return `${t.slice(0, max - 1).trimEnd()}…`;
    },
    getClientName(clientId) {
      if (!clientId) return '';
      const client = this.clients.find(c => c.id === clientId);
      return client ? client.name : '';
    },
    async toggleArchivedView() {
      await this.fetchProjects();
    },
    async fetchProjects() {
      this.loading = true;
      this.error = null;
      try {
        const params = this.showArchived ? { show_archived: 'true' } : {};
        const response = await api.get('/projects/', { params });

        let projectsData = [];
        if (response && response.data) {
          if (Array.isArray(response.data.results)) {
            projectsData = response.data.results;
          } else if (Array.isArray(response.data)) {
            projectsData = response.data;
          } else if (Array.isArray(response.data.projects)) {
            projectsData = response.data.projects;
          }
        }

        this.projects = projectsData.map(p => ({
          ...p,
          client_name: p.client?.name || 'N/A',
          project_type_display: p.project_type_display || p.project_type,
          default_comprehensive_check_name: p.default_comprehensive_check_name || null
        }));
      } catch (error) {
        console.error('Error fetching projects:', error);
        this.error = error.message || 'Failed to load projects';
        this.showError(this.error);
        this.projects = [];
      } finally {
        this.loading = false;
      }
    },
    async fetchClients() {
      this.error = null;
      try {
        const response = await api.get('/clients/');
        this.clients = response.data || [];
      } catch (error) {
        console.error('Error fetching clients:', error);
        this.error = error.message || 'Failed to load clients';
        this.showError(this.error);
      }
    },
    async createProject() {
      try {
        const projectData = {
          name: this.projectForm.name,
          description: this.projectForm.description,
          project_type: 'GENERAL',
          client: this.projectForm.client,
          enable_comprehensive_checks: true
        };

        const response = await api.post('/projects/', projectData);
        const responseData = response.data || response;
        if (responseData.success && responseData.project) {
          const newProject = responseData.project;
          this.showCreateModal = false;
          this.resetForm();
          this.showNotification('Project created! Configure analysis settings...', 'success');
          this.$router.push(`/projects/${newProject.id}/settings`);
        } else {
          throw new Error(responseData.error || 'Failed to create project');
        }
      } catch (error) {
        console.error('Error creating project:', error);
        this.showError(error.message || 'Error creating project');
      }
    },
    async deleteProject(projectId) {
      if (!confirm('Are you sure you want to delete this project?')) {
        return;
      }
      try {
        await api.delete(`/projects/${projectId}/delete/`);
        this.showNotification('Project deleted successfully', 'success');
        this.fetchProjects();
      } catch (error) {
        console.error('Error deleting project:', error);
        this.showError(error.message || 'Error deleting project');
      }
    },
    showNotification(message, type = 'success') {
      this.notification = {
        show: true,
        message,
        type
      };
      setTimeout(() => {
        this.notification.show = false;
      }, 3000);
    },
    editProject(project) {
      this.editingProject = {
        id: project.id,
        name: project.name,
        description: project.description,
        project_type: project.project_type
      };
      this.showEditModal = true;
    },
    async updateProject() {
      try {
        const response = await api.put(`/projects/${this.editingProject.id}/update/`, {
          name: this.editingProject.name,
          description: this.editingProject.description,
          project_type: this.editingProject.project_type
        });

        if (response.success && response.project) {
          const index = this.projects.findIndex(p => p.id === this.editingProject.id);
          if (index !== -1) {
            this.projects[index] = response.project;
          }
          this.showEditModal = false;
          this.showNotification('Project updated successfully', 'success');
        }
      } catch (error) {
        console.error('Error updating project:', error);
        this.showError(error.message || 'Error updating project');
      }
    },
    async createClient() {
      try {
        const clientData = {
          name: this.clientForm.name,
          email: this.clientForm.email,
          phone: this.clientForm.phone || '',
          description: this.clientForm.description || '',
          industry_type: this.clientForm.industry_type,
          business_type: this.clientForm.business_type,
          status: 'ACTIVE'
        };

        const response = await api.post('/clients/', clientData);
        const newClient = response.data || response;
        this.clients.push(newClient);
        this.projectForm.client = newClient.id;
        this.showClientModal = false;
        this.clientForm = {
          name: '',
          email: '',
          phone: '',
          description: '',
          industry_type: '',
          business_type: '',
          status: 'ACTIVE'
        };
        this.showNotification('Client created successfully', 'success');
      } catch (error) {
        console.error('Error creating client:', error);
        this.showError(error.message || 'Error creating client');
      }
    },
    resetForm() {
      this.projectForm = {
        name: '',
        description: '',
        client: null
      };
    },
    showError(message) {
      this.notification = {
        show: true,
        message: message,
        type: 'error'
      };
      setTimeout(() => {
        this.notification.show = false;
      }, 5000);
    },
    closeModal() {
      this.showCreateModal = false;
    }
  }
}
</script>

<style scoped>
/* Page chrome uses global .entity-list-page / .entity-card-grid (spoke-entity-cards) */

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 1.25rem 1.35rem 1.35rem;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
}

.modal-content h2 {
  margin: 0 0 1rem;
  font-size: 1.15rem;
  font-weight: 650;
}

.form-group {
  margin-bottom: 0.9rem;
}

.form-group label {
  display: block;
  font-size: 0.8rem;
  font-weight: 550;
  margin-bottom: 0.35rem;
  color: #0f172a;
}

.form-control {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.55rem 0.7rem;
  font: inherit;
  font-size: 0.9rem;
  color: #0f172a;
  background: #fff;
}

.form-control:focus {
  outline: none;
  border-color: var(--p-accent, #1e3a5f);
  box-shadow: 0 0 0 3px rgba(30, 58, 95, 0.12);
}

.form-hint {
  margin: 0.25rem 0 0;
  font-size: 0.8rem;
  color: #64748b;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 1.1rem;
}
</style>
